import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import { createBootcamp } from '../../api/bootcampApi';
import Card    from '../../components/ui/Card';
import Spinner from '../../components/ui/Spinner';

export default function CreateBootcampPage() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { status: 'draft' },
  });

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const payload = { ...data };
      if (!payload.cohort_name)  delete payload.cohort_name;
      if (!payload.description)  delete payload.description;
      if (!payload.start_date)   delete payload.start_date;
      if (!payload.end_date)     delete payload.end_date;

      await createBootcamp(payload);
      toast.success('Bootcamp created!');
      navigate('/bootcamps');
    } catch (err) {
      const msg = err?.response?.data?.message ?? 'Failed to create bootcamp';
      const errs = err?.response?.data?.errors ?? {};
      if (Object.keys(errs).length) {
        Object.values(errs).flat().forEach((e) => toast.error(e));
      } else {
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      {/* Back + heading */}
      <div>
        <Link
          to="/bootcamps"
          className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
        >
          ← Back to Bootcamps
        </Link>
        <h1 className="mt-3 text-2xl font-bold font-heading text-neutral-900">New Bootcamp</h1>
        <p className="mt-0.5 text-sm text-neutral-500">
          Create a cohort or program to group your assessments.
        </p>
      </div>

      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">

          {/* Title */}
          <div>
            <label className="label-base" htmlFor="title">
              Title <span className="text-error-500">*</span>
            </label>
            <input
              id="title"
              {...register('title', {
                required: 'Title is required',
                minLength: { value: 3, message: 'Minimum 3 characters' },
                maxLength: { value: 200, message: 'Maximum 200 characters' },
              })}
              className={clsx('input-base', errors.title && 'border-error-400 focus:border-error-500 focus:ring-error-500')}
              placeholder="e.g. Cohort 4 — Fintech Track"
            />
            {errors.title && <p className="mt-1 text-xs text-error-600">{errors.title.message}</p>}
          </div>

          {/* Cohort name */}
          <div>
            <label className="label-base" htmlFor="cohort_name">
              Cohort Name <span className="text-neutral-400 text-xs font-normal">(optional)</span>
            </label>
            <input
              id="cohort_name"
              {...register('cohort_name', {
                maxLength: { value: 150, message: 'Maximum 150 characters' },
              })}
              className={clsx('input-base', errors.cohort_name && 'border-error-400')}
              placeholder="e.g. Spring 2026"
            />
            {errors.cohort_name && (
              <p className="mt-1 text-xs text-error-600">{errors.cohort_name.message}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="label-base" htmlFor="description">
              Description <span className="text-neutral-400 text-xs font-normal">(optional)</span>
            </label>
            <textarea
              id="description"
              rows={3}
              {...register('description')}
              className="input-base resize-none"
              placeholder="Briefly describe this bootcamp or cohort…"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label className="label-base" htmlFor="start_date">Start Date</label>
              <input
                id="start_date"
                type="date"
                {...register('start_date')}
                className="input-base"
              />
            </div>
            <div>
              <label className="label-base" htmlFor="end_date">End Date</label>
              <input
                id="end_date"
                type="date"
                {...register('end_date')}
                className="input-base"
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="label-base" htmlFor="status">Status</label>
            <select id="status" {...register('status')} className="input-base">
              <option value="draft">Draft</option>
              <option value="active">Active</option>
            </select>
            <p className="mt-1 text-xs text-neutral-400">
              Set to <strong>Active</strong> to make it selectable when creating assessments.
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 border-t border-neutral-100 pt-4">
            <Link
              to="/bootcamps"
              className="rounded-button border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-button bg-primary-600 px-5 py-2 text-sm font-semibold text-white shadow-button hover:bg-primary-700 disabled:opacity-60 transition"
            >
              {saving && <Spinner size="sm" className="text-white" />}
              {saving ? 'Creating…' : 'Create Bootcamp'}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}
